/**
 * Lesson completion — the learner's local record, and the write to the system
 * of record.
 *
 * ## A lesson has TWO STAGES
 *
 * `guided` is the coach-mark tour (`TourOverlay`); `unguided` is the task run
 * the learner drives themselves (`TaskOverlay`). The authored flow is
 * guided → unguided → complete, and finishing the guided stage alone MUST still
 * be saved — it is real progress — without marking the lesson complete.
 * Finishing the unguided stage completes the lesson on its own, guided run or
 * not (see `isLessonComplete`).
 *
 * The exception is the common case, not the edge case: only
 * `ordering-essentials` and `receiving-essentials` author `tasks[]`, so 19 of
 * the 29 sub-modules have no unguided stage at all. For those, guided alone IS
 * completion — without that rule they could never complete and every course
 * percentage would be stuck below 100%. `requiresUnguided` is what carries the
 * distinction, and it is supplied by the overlay, which has the sub-module
 * loaded already (`hasUnguidedStage` in `./tour-content.ts`).
 *
 * ## Where the record lives
 *
 * Completion is kept in `localStorage` (zustand `persist`, key
 * `lesson-progress:v1`) and, when the host app identified the session, posted to
 * `POST /api/progress/completions` so it reaches the Google Sheet.
 *
 * The local record is the primary one: a learner who finished a lesson has
 * finished it whether or not the Sheet ever hears about it. What the Sheet adds
 * is portability — `hydrateStages` folds the server's record back in at boot
 * (`src/session/hydrate.ts`) so a learner who switches devices does not start
 * from zero. TECH-SPEC.md:1295: server = truth for COMPLETION, local = truth
 * for POSITION.
 */

import type { Completion, LessonStage } from '@shared/api-types'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { postCompletions } from '@/api/progress-client'
import type { ModuleIdLookup } from '@/player/tour-content'
import { findModuleIdFor } from '@/player/tour-content'
import { getSessionIdentity } from '@/session/identity'

/**
 * Re-exported rather than redeclared: the wire contract in
 * `@shared/api-types` is the one definition, and a second literal union here
 * would silently diverge from it the first time a stage is added.
 */
export type { LessonStage }

/** What this store knows about one lesson's two stages. */
export interface StageRecord {
  /** ISO `completedAt` of the guided tour. Absent = not finished. */
  guided?: string
  /** ISO `completedAt` of the unguided task run. Absent = not finished. */
  unguided?: string
  /**
   * Whether the lesson HAD an unguided stage when this was recorded — stored,
   * not re-derived from content on read, exactly as the Sheet row stores it
   * (`server/sheets/rows.ts`). It is what lets completion be decided
   * synchronously here, with no content load, and what stops a later content
   * edit from retroactively un-completing a lesson somebody already finished.
   */
  requiresUnguided: boolean
}

/**
 * What became of the attempt to report ONE stage to the system of record.
 *
 * The post is fired without `await` (see `markStageDone`), so until this
 * existed nothing could observe it and any "saving…" indicator in the UI would
 * have been a decoration that never changed. The completion screen shows this
 * value, so a report that did not land must never look like one that did.
 *
 * Deliberately NOT persisted: it describes one attempt in one session, and a
 * `pending` written to `localStorage` would come back as a spinner that can
 * never resolve. A stage with no entry here is simply one this session did not
 * report — which the UI says, rather than guessing.
 */
export type StageReportStatus =
  /** The POST is in flight. */
  | 'pending'
  /** The server took it. Buffered for the Sheet, not yet in it. */
  | 'recorded'
  /**
   * Nothing was sent, on purpose: the session is unidentified (BOOT-03), or
   * the sub-module id does not resolve to exactly one module. The learner's
   * local record stands; the Sheet will never hear about it.
   */
  | 'local-only'
  /**
   * It was sent and did not land — rejected, unavailable, unreachable, or the
   * report itself threw. There is no retry and no outbox, so this one is lost.
   */
  | 'failed'

/** Key into `reports`. A report is per (lesson, stage), exactly like the dedupe. */
export function stageReportKey(lessonId: string, stage: LessonStage): string {
  return `${lessonId}:${stage}`
}

interface LessonProgressState {
  stages: Record<string, StageRecord>
  /** Derived on every mutation: ids of lessons that are FULLY complete. */
  completed: string[]
  /** `stageReportKey(...)` → how this session's report for that stage turned out. */
  reports: Record<string, StageReportStatus>
  markStageDone: (lessonId: string, stage: LessonStage, requiresUnguided: boolean) => void
  hydrateStages: (completions: readonly Completion[]) => void
}

/** The slice written to `localStorage`. `completed` is derived, so it is not in it. */
interface PersistedLessonProgress {
  stages: Record<string, StageRecord>
}

/**
 * unguided done, OR guided done on a lesson with no unguided stage.
 *
 * An unguided run on its own completes the lesson: "Try it without guidance"
 * is offered before the guided tour, and a learner who can do the task unaided
 * has shown more than the tour teaches. The guided stage is IMPLIED for
 * display only — no `guided` stamp is written, so the Sheet still records what
 * the learner actually ran. This is also the rule the server's row model
 * documents (`stage === 'unguided' || !requiresUnguided`, server/sheets/rows.ts).
 */
export function isLessonComplete(record: StageRecord | undefined): boolean {
  if (record?.unguided !== undefined) return true
  return record?.guided !== undefined && !record.requiresUnguided
}

/**
 * THE INVARIANT: `completed` is recomputed from `stages` here and nowhere else,
 * on every path that writes `stages` — mutation, hydration and rehydration.
 *
 * It stays a flat array of FULLY complete ids on purpose: it is the one place
 * "is this lesson done" is decided, so nothing downstream re-derives it and
 * disagrees. The dashboard reads `stages` for its stage-weighted percentages
 * but calls `isLessonComplete` for done-ness, and the Sheet projection applies
 * the same rule server-side.
 */
function completedIdsOf(stages: Record<string, StageRecord>): string[] {
  return Object.entries(stages)
    .filter(([, record]) => isLessonComplete(record))
    .map(([lessonId]) => lessonId)
}

/** Whether this stage is already recorded — the per-(lesson, stage) dedupe. */
function hasStage(record: StageRecord | undefined, stage: LessonStage): boolean {
  return record?.[stage] !== undefined
}

/**
 * `record` with `stage` stamped at `completedAt`. Never clears the other stage,
 * so the two stages can arrive in either order — an unguided-first run keeps
 * its stamp when the guided stage lands later.
 *
 * `requiresUnguided` is OR-merged rather than overwritten. It can therefore
 * never flip true → false, and a flip false → true can only ever come from an
 * `unguided` record, which supplies the missing stage in the same breath — so
 * this cannot un-complete a lesson that was already complete.
 */
function withStage(
  record: StageRecord | undefined,
  stage: LessonStage,
  completedAt: string,
  requiresUnguided: boolean,
): StageRecord {
  const merged: StageRecord = {
    ...record,
    requiresUnguided: record?.requiresUnguided === true || requiresUnguided,
  }
  return stage === 'guided'
    ? { ...merged, guided: completedAt }
    : { ...merged, unguided: completedAt }
}

/**
 * Sent as `contentRevision` on every completion.
 *
 * **The client does not have a content revision.** TECH-SPEC §3.10/§4.9 sources
 * one from the generated content bundles (`src/content/.generated/`), a pipeline
 * that does not exist yet — `tour-content.ts` globs `src/content/modules/*.json`
 * directly. There is no `import.meta.env` value for it, no `define` in
 * `vite.config.ts`, and no constant anywhere in `src/`. The server's
 * `CONTENT_REVISION` (`server/config.ts:107`) is a runtime env var the browser
 * cannot see.
 *
 * `CompletionsRequestSchema` declares the field `z.string().min(1)`
 * (`server/routes/progress.ts:29`), so it cannot be omitted. This literal is
 * deliberately NOT hash-shaped: it is written to column H of the Sheet, and
 * whoever reads it there should see that the client had nothing to send rather
 * than a plausible-looking revision that quietly means the wrong thing.
 *
 * Replace this the moment the generated-bundle pipeline lands.
 */
export const UNSET_CONTENT_REVISION = 'client-unset'

/** Why a sub-module id could not be mapped to exactly one module. */
function describeLookupFailure(lookup: Exclude<ModuleIdLookup, { status: 'resolved' }>): string {
  return lookup.status === 'ambiguous'
    ? `its id belongs to more than one module (${lookup.moduleIds.join(', ')})`
    : 'no module in the curriculum contains it'
}

/**
 * Report one completed STAGE to the server. Resolves for every outcome and
 * never rejects — it is called without `await`, from a React effect with no
 * guard of its own.
 *
 * It RESOLVES TO WHAT HAPPENED rather than to nothing. The caller writes that
 * into `reports` so the completion screen can tell the learner the truth; the
 * value is the only record of this attempt, because none of these outcomes is
 * retried.
 */
async function reportCompletion(
  subModuleId: string,
  stage: LessonStage,
  requiresUnguided: boolean,
  completedAt: string,
): Promise<StageReportStatus> {
  try {
    // `identity === null` is exactly `!isIdentified()` — both read the same
    // value captured at boot. Going through `getSessionIdentity()` also narrows
    // the type, which `isIdentified()` cannot do and `noNonNullAssertion`
    // forbids working around.
    //
    // BOOT-03 / TECH-SPEC §2.1: an unidentified session may browse the sandbox
    // freely but must never write. A row keyed on nobody can never be
    // reconciled against the client's HR data, and the Sheet is the system of
    // record — so record locally and send nothing.
    const identity = getSessionIdentity()
    if (identity === null) return 'local-only'

    // `markStageDone` is handed a sub-module id, but a Sheet row needs its
    // module too — and sub-module ids are NOT unique: `lesson-4` exists in both
    // `soh-adjustment.json` and `stocktake-essentials.json`. Guessing files a
    // completion under the wrong lesson column permanently, so an unresolved id
    // is skipped instead. A missing row can be recovered; a wrong one cannot.
    const lookup = await findModuleIdFor(subModuleId)
    if (lookup.status !== 'resolved') {
      console.warn(
        `lesson-progress: not reporting "${subModuleId}" — ${describeLookupFailure(lookup)}. It is recorded locally only.`,
      )
      return 'local-only'
    }

    // NOTE: `identity.roleId` may be '' — `readIdentity` returns a role-less
    // identity on purpose (src/session/identity.ts) — while the server declares
    // `roleId: z.string().min(1)`. Such a post comes back `rejected`. Sending
    // what identity actually holds, rather than inventing a placeholder role,
    // keeps that contract mismatch visible instead of writing a fiction to the
    // system of record.
    const result = await postCompletions({
      employeeId: identity.employeeId,
      roleId: identity.roleId,
      contentRevision: UNSET_CONTENT_REVISION,
      events: [
        {
          clientEventId: crypto.randomUUID(),
          moduleId: lookup.moduleId,
          subModuleId,
          // The same instant the local record was stamped with, not a second
          // clock read — local and Sheet must not disagree about when this
          // happened.
          completedAt,
          stage,
          // The caller's value, NOT the OR-merged one from the store: the row
          // records what the content looked like at the moment the learner
          // finished this stage.
          requiresUnguided,
        },
      ],
    })

    // A COMPLETION THAT FAILS TO SEND IS STILL LOST. There is no retry, no
    // outbox and no flush on reload, so even a `retryable` result gets no
    // second chance. The trade is deliberate for now — the server buffers
    // everything it accepts (`server/sheets/writer.ts`), completions are
    // idempotent on `clientEventId`, and the learner's own record survives in
    // localStorage — but nothing here recovers a post that never landed. What
    // has changed is that the failure is no longer SILENT: it is returned, and
    // the completion screen says so rather than showing the learner a tick.
    if (result.status !== 'accepted') {
      console.warn(
        `lesson-progress: "${subModuleId}" ${stage} was recorded locally but not sent — ${result.detail}`,
      )
      return 'failed'
    }
    return 'recorded'
  } catch (err) {
    // `postCompletions` never rejects, but this function has two other ways to
    // fail: the dynamic content imports behind `findModuleIdFor` (a chunk that
    // will not load on store wifi) and `crypto.randomUUID`, which is absent
    // outside a secure context. Neither may surface as an unhandled rejection —
    // `TaskOverlay.tsx` calls `markStageDone` straight from an effect with no
    // guard of its own.
    console.warn(`lesson-progress: could not report "${subModuleId}" —`, err)
    return 'failed'
  }
}

/** localStorage key. Unchanged — real users and the UAT device have data here. */
const STORAGE_KEY = 'lesson-progress:v1'

/**
 * Persist schema version. 0 is the shape that shipped: `{ completed: string[] }`
 * with no `version` key at all, which zustand reads back as version 0.
 */
const PERSIST_VERSION = 1

/**
 * The `completedAt` given to a lesson recovered from the v0 payload.
 *
 * v0 stored ids only — it never had a timestamp to keep. The epoch is used
 * deliberately instead of "now": it is visibly not a real completion time, so
 * nothing downstream can mistake a migrated record for something the learner
 * did at boot. Like `UNSET_CONTENT_REVISION`, it would rather look empty than
 * look plausible and be wrong.
 */
export const MIGRATED_COMPLETED_AT = new Date(0).toISOString()

/** The `stages` map inside a persisted payload, or null when it holds none. */
function stagesOf(persisted: unknown): Record<string, StageRecord> | null {
  return (persisted as PersistedLessonProgress | null | undefined)?.stages ?? null
}

/**
 * THE v0 MIGRATION.
 *
 * Under v0 an id in `completed` meant exactly one thing: "this lesson is done".
 * There was no unguided stage for it to be missing, so the only reading that
 * does not retroactively un-complete work the learner already did is
 * guided-done with no unguided stage required — which is COMPLETE. That is the
 * same choice the server makes for v1 sheet rows (`server/sheets/rows.ts`), and
 * the two must agree or a migrated learner's dashboard would disagree with
 * their Sheet.
 *
 * Defensive about the payload because it comes from `localStorage`, which any
 * device owner can hand-edit: anything that is not a string id is dropped
 * rather than trusted into the store.
 */
function migrateFromV0(persisted: unknown): PersistedLessonProgress {
  const raw = (persisted as { completed?: unknown } | null | undefined)?.completed
  const stages: Record<string, StageRecord> = {}
  for (const lessonId of Array.isArray(raw) ? raw : []) {
    if (typeof lessonId === 'string') {
      stages[lessonId] = { guided: MIGRATED_COMPLETED_AT, requiresUnguided: false }
    }
  }
  return { stages }
}

export const useLessonProgress = create<LessonProgressState>()(
  persist<LessonProgressState, [], [], PersistedLessonProgress>(
    (set, get) => ({
      stages: {},
      completed: [],
      reports: {},

      markStageDone: (lessonId, stage, requiresUnguided) => {
        const { stages } = get()

        // THE DEDUPE — AND THE REASON THE POST LIVES IN HERE RATHER THAN AT THE
        // CALL SITES.
        //
        // Two paths finish a lesson: `finish()` in `TourOverlay.tsx` when a
        // guided tour runs past its last step, and the `allDone` effect in
        // `TaskOverlay.tsx` when every task of an unguided run is ticked. Either
        // can fire more than once for one stage — a re-render, a replayed
        // lesson, a learner who reaches the last step twice — so hooking the
        // call sites would write duplicate rows. Firing from inside here,
        // *after* this check, fires exactly once per (lesson, stage) however
        // many callers arrive.
        //
        // Abandoning a lesson (`exit()` on either overlay) never calls this, so
        // it stays the non-event it must be.
        if (hasStage(stages[lessonId], stage)) return

        const completedAt = new Date().toISOString()
        const next = {
          ...stages,
          [lessonId]: withStage(stages[lessonId], stage, completedAt, requiresUnguided),
        }

        // Local record first — it is the learner's, and it must stand whether
        // or not the post succeeds or is even attempted. If this throws
        // (zustand's `persist` calls `localStorage.setItem` unguarded, which is
        // why `TourOverlay.finish` wraps the call) nothing is reported, and the
        // completion falls into the same lost bucket described above.
        //
        // `pending` is written in the SAME set as the record, before the report
        // starts. The completion screen mounts within a tick of this call, and
        // a screen that read `undefined` first would flash "saved on this
        // device" and only then admit it was still trying.
        const reportKey = stageReportKey(lessonId, stage)
        set({
          stages: next,
          completed: completedIdsOf(next),
          reports: { ...get().reports, [reportKey]: 'pending' },
        })

        // Not awaited: this runs inside a React effect/handler, and finishing a
        // lesson must never wait on — or be broken by — the network. `void`
        // marks the floating promise intentional for `nursery/noFloatingPromises`,
        // which is an error in this repo. `reportCompletion` resolves for every
        // outcome and never rejects, so `.then` alone is exhaustive.
        void reportCompletion(lessonId, stage, requiresUnguided, completedAt).then((status) => {
          // Re-read rather than close over the snapshot above: a second stage
          // can settle between the two, and `{ ...stale }` would erase it.
          set({ reports: { ...get().reports, [reportKey]: status } })
        })
      },

      /**
       * Fold the server's record of this learner's stages into the local one.
       * Called once at boot by `src/session/hydrate.ts` — this is what makes
       * progress follow a learner to a second device.
       *
       * DO NOT REPLACE THIS WITH A LOOP OVER `markStageDone`. The two actions
       * look interchangeable and are not: `markStageDone` fires
       * `reportCompletion()` past its dedupe check, so feeding it freshly
       * downloaded records would immediately re-upload every one of them — a
       * POST per stage, on boot, for rows the Sheet already has. This action
       * writes state directly and sends NOTHING.
       *
       * MERGE, NEVER REPLACE. A stage finished offline (or by an unidentified
       * session, which never posts at all — BOOT-03) exists only in
       * localStorage; overwriting `stages` with the server's list would delete
       * it from the one place it lives. Local order is preserved, local stamps
       * win, and server-only ids are appended — so the result is a union,
       * idempotent however many times and in whatever order the two sources
       * arrive.
       */
      hydrateStages: (completions) => {
        const { stages } = get()
        const next = { ...stages }
        let changed = false

        for (const { subModuleId, stage, completedAt, requiresUnguided } of completions) {
          if (hasStage(next[subModuleId], stage)) continue
          next[subModuleId] = withStage(next[subModuleId], stage, completedAt, requiresUnguided)
          changed = true
        }

        // Nothing new: return without calling `set`, so a repeat hydration
        // neither re-renders the dashboard nor rewrites localStorage.
        if (!changed) return

        set({ stages: next, completed: completedIdsOf(next) })
      },
    }),
    {
      name: STORAGE_KEY,
      version: PERSIST_VERSION,

      // Only `stages` is written. `completed` is derived, and persisting a
      // derived value is how the two drift apart.
      partialize: ({ stages }) => ({ stages }),

      migrate: (persisted, version) => {
        if (version === 0) return migrateFromV0(persisted)
        // A version this build has never written — the only way to get one is
        // to roll a device back from a newer build. Its `stages` are read
        // best-effort rather than discarded, because discarding is the one
        // outcome that loses the learner's record for good.
        return { stages: stagesOf(persisted) ?? {} }
      },

      // `merge` is where rehydration re-derives `completed`, so it can never be
      // stale on boot. zustand calls this on EVERY hydrate — including when
      // storage is empty, where `persisted` is `undefined` — and then
      // `set(..., true)` REPLACES state, so the result must carry the actions
      // through from `current`.
      merge: (persisted, current) => {
        const stages = stagesOf(persisted)
        if (stages === null) return current
        return { ...current, stages, completed: completedIdsOf(stages) }
      },
    },
  ),
)
